import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Grid, Stack, TextField, Typography } from '@mui/material';
import AuthShell from '../components/AuthShell';
import LoadingFallback from '../components/LoadingFallback';
import { api, apiErrorMessage } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface InviteInfo {
  email: string;
  role: string;
  first_name?: string | null;
  last_name?: string | null;
  expires_at: string;
}

export default function AcceptInvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        if (!cancelled) {
          setInvite(data);
          setFirstName(data.first_name || '');
          setLastName(data.last_name || '');
        }
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, 'Invite is invalid or expired.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await acceptInvite(token, {
        first_name: firstName,
        last_name: lastName,
        password,
        phone: phone || undefined,
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not accept invite.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AuthShell title="Accept invite">
        <LoadingFallback height={120} />
      </AuthShell>
    );
  }

  if (!invite) {
    return (
      <AuthShell title="Invite unavailable">
        <Alert severity="error">{error || 'Invite is invalid or expired.'}</Alert>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set up your account"
      subtitle={`You're joining as a ${invite.role}.`}
    >
      <form onSubmit={onSubmit} aria-label="Accept invite form">
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Email: <strong>{invite.email}</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                inputProps={{ 'aria-label': 'First name' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                inputProps={{ 'aria-label': 'Last name' }}
              />
            </Grid>
          </Grid>
          <TextField
            label="Phone (optional)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputProps={{ 'aria-label': 'Phone' }}
          />
          <TextField
            label="Password (min 8 chars)"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            inputProps={{ 'aria-label': 'Password' }}
          />
          <Button type="submit" variant="contained" size="large" disabled={busy} aria-label="Accept invite">
            {busy ? 'Creating account…' : 'Accept invite'}
          </Button>
        </Stack>
      </form>
    </AuthShell>
  );
}
